import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, CornerDownLeft, Search, X } from 'lucide-react';
import {
  activeNavKeys,
  activeNavPath,
  groupKey,
  matchingNavItems,
  sectionEntries,
  sectionKey,
  type NavEntry,
  type NavItem,
  type NavSection,
} from '@/lib/navigation';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';

const STORAGE_KEY = 'his.nav.expanded';
const COMPACT_LIMIT = 14;

function readStoredExpansion(): Set<string> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set<string>(JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

function storeExpansion(keys: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    return;
  }
}

interface TreeState {
  collapsible: boolean;
  expanded: Set<string>;
  activePath?: string;
  activeKeys: string[];
  setOpen: (key: string, open: boolean) => void;
  onNavigate: () => void;
}

export function SidebarNav({
  sections,
  onNavigate,
}: {
  sections: NavSection[];
  onNavigate: () => void;
}) {
  const common = useTranslation('common');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const itemCount = sections.reduce((total, section) => total + section.items.length, 0);
  const collapsible = sections.length > 1 && itemCount > COMPACT_LIMIT;
  const activePath = activeNavPath(sections, pathname);
  const activeKeys = activeNavKeys(sections, pathname);
  const activeSignature = activeKeys.join('\n');

  const [expanded, setExpanded] = useState<Set<string>>(
    () => readStoredExpansion() ?? new Set(activeKeys),
  );

  useEffect(() => {
    if (!activeSignature) return;
    setExpanded((current) => {
      const wanted = activeSignature.split('\n');
      if (wanted.every((key) => current.has(key))) return current;
      const next = new Set([...current, ...wanted]);
      storeExpansion(next);
      return next;
    });
  }, [pathname, activeSignature]);

  const setOpen = (key: string, open: boolean) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (open) next.add(key);
      else next.delete(key);
      storeExpansion(next);
      return next;
    });

  const clearAndClose = () => {
    setQuery('');
    onNavigate();
  };

  const searching = query.trim().length > 0;
  const matches = matchingNavItems(sections, query);
  const tree: TreeState = { collapsible, expanded, activePath, activeKeys, setOpen, onNavigate };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {collapsible && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && matches[0]) {
                  navigate(matches[0].item.to);
                  clearAndClose();
                }
                if (event.key === 'Escape') setQuery('');
              }}
              placeholder={common.t('nav.find_screen')}
              aria-label={common.t('nav.find_screen_label')}
              className={cn(
                'h-8 w-full rounded-md border border-input bg-muted/40 pe-7 ps-8 text-[13px] text-foreground outline-none',
                'transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-muted-foreground/80',
                'hover:border-muted-foreground/40 focus-visible:border-ring focus-visible:bg-background focus-visible:ring-[3px] focus-visible:ring-ring/25',
                '[&::-webkit-search-cancel-button]:appearance-none',
              )}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={common.t('nav.clear_search')}
                className="absolute end-1.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:text-foreground focus-ring"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <nav aria-label="Main" className="flex-1 space-y-0.5 overflow-y-auto p-3">
        {searching ? (
          <SearchResults
            matches={matches}
            query={query}
            activePath={activePath}
            onPick={clearAndClose}
          />
        ) : (
          sections.map((section) => (
            <SectionBlock key={section.title} section={section} tree={tree} />
          ))
        )}
      </nav>
    </div>
  );
}

function SectionBlock({ section, tree }: { section: NavSection; tree: TreeState }) {
  const key = sectionKey(section);
  const list = (
    <ul className="space-y-0.5 pb-2 pt-0.5">
      {sectionEntries(section).map((entry) => (
        <li key={entry.kind === 'item' ? entry.item.to : entry.name}>
          {entry.kind === 'item' ? (
            <NavEntryLink
              item={entry.item}
              active={entry.item.to === tree.activePath}
              onNavigate={tree.onNavigate}
            />
          ) : (
            <GroupBlock section={section} group={entry} tree={tree} />
          )}
        </li>
      ))}
    </ul>
  );

  if (!tree.collapsible) {
    return (
      <div>
        <p className="px-2 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
          {section.title}
        </p>
        {list}
      </div>
    );
  }

  const open = tree.expanded.has(key);
  const holdsActive = tree.activeKeys.includes(key);

  return (
    <Collapsible open={open} onOpenChange={(next) => tree.setOpen(key, next)}>
      <CollapsibleTrigger
        className={cn(
          'group flex w-full items-center gap-2 rounded-md px-2 py-2 text-[11px] font-semibold uppercase tracking-wider lg:py-1.5',
          'text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-ring',
        )}
      >
        <span className="flex-1 truncate text-start">{section.title}</span>
        {!open && holdsActive && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
        )}
        <ChevronDown
          className="h-3.5 w-3.5 shrink-0 -rotate-90 transition-transform duration-200 rtl:rotate-90 group-data-[state=open]:rotate-0"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>{list}</CollapsibleContent>
    </Collapsible>
  );
}

function GroupBlock({
  section,
  group,
  tree,
}: {
  section: NavSection;
  group: Extract<NavEntry, { kind: 'group' }>;
  tree: TreeState;
}) {
  const key = groupKey(section, group.name);
  const Icon = group.icon;
  const children = (
    <ul className="ms-4 mt-0.5 space-y-0.5 border-s border-border pb-1 ps-2">
      {group.items.map((item) => (
        <li key={item.to}>
          <NavEntryLink
            item={item}
            nested
            active={item.to === tree.activePath}
            onNavigate={tree.onNavigate}
          />
        </li>
      ))}
    </ul>
  );

  if (!tree.collapsible) {
    return (
      <div>
        <div className="flex items-center gap-2.5 px-2 py-1.5 text-sm text-muted-foreground">
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{group.name}</span>
        </div>
        {children}
      </div>
    );
  }

  const open = tree.expanded.has(key);
  const holdsActive = tree.activeKeys.includes(key);

  return (
    <Collapsible open={open} onOpenChange={(next) => tree.setOpen(key, next)}>
      <CollapsibleTrigger
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors focus-ring lg:py-1.5',
          'hover:bg-accent hover:text-accent-foreground',
          holdsActive && !open ? 'font-medium text-primary' : 'text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="flex-1 truncate text-start">{group.name}</span>
        <ChevronDown
          className="h-3.5 w-3.5 shrink-0 -rotate-90 opacity-60 transition-transform duration-200 rtl:rotate-90 group-data-[state=open]:rotate-0"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}

function NavEntryLink({
  item,
  active,
  nested,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  nested?: boolean;
  onNavigate: () => void;
}) {
  const common = useTranslation('common');
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex items-center gap-2.5 rounded-md px-2 text-sm transition-colors focus-ring',
        nested ? 'py-1.5 lg:py-1' : 'py-2 lg:py-1.5',
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        active &&
          (nested
            ? 'before:absolute before:-start-[9px] before:inset-y-1 before:w-0.5 before:rounded-full before:bg-primary'
            : 'before:absolute before:-start-3 before:inset-y-1.5 before:w-[3px] before:rounded-e-full before:bg-primary'),
      )}
    >
      <Icon className={cn('shrink-0', nested ? 'h-[15px] w-[15px]' : 'h-4 w-4')} aria-hidden />
      <span className="truncate">{item.label}</span>
      {item.readOnly && (
        <span className="ms-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{common.t('nav.read_only')}</span>
      )}
    </Link>
  );
}

function SearchResults({
  matches,
  query,
  activePath,
  onPick,
}: {
  matches: { section: string; item: NavItem }[];
  query: string;
  activePath?: string;
  onPick: () => void;
}) {
  const common = useTranslation('common');
  if (matches.length === 0) {
    return (
      <p className="px-2 py-8 text-center text-sm text-muted-foreground">
        {common.t('nav.no_screen_matches', { query: query.trim() })}
      </p>
    );
  }
  return (
    <ul className="space-y-0.5">
      {matches.map(({ section, item }, index) => {
        const Icon = item.icon;
        return (
          <li key={item.to}>
            <Link
              to={item.to}
              onClick={onPick}
              aria-current={item.to === activePath ? 'page' : undefined}
              className={cn(
                'group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors focus-ring',
                item.to === activePath
                  ? 'bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                index === 0 && item.to !== activePath && 'bg-accent/60 text-foreground',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{item.label}</span>
              {index === 0 ? (
                <CornerDownLeft
                  className="h-3.5 w-3.5 shrink-0 opacity-60"
                  aria-label={common.t('nav.press_enter')}
                />
              ) : (
                <span className="shrink-0 text-[11px] text-muted-foreground/70">
                  {item.group ?? section}
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
